/**
 * Legacy Subtasks section — shown inside a parent task's detail sheet when the
 * task has children from the retired subtask system. Finish-only: status can
 * be advanced and comments added, but NEW subtasks can never be created.
 */
import { useState, useEffect, useCallback } from 'react';
import { taskAPI } from '../lib/api';
import api from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Loader2, Layers, Play, CheckCircle2, MessageSquare, Send } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABEL = {
  not_started: 'Not Started', in_progress: 'In Progress', waiting: 'Waiting',
  review: 'Review', completed: 'Completed', cancelled: 'Cancelled',
};
const STATUS_CLS = {
  completed: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  in_progress: 'bg-gold/15 text-gold border-gold/40',
  review: 'bg-blue-500/15 text-blue-300 border-blue-500/40',
  waiting: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
};

export function LegacySubtasksSection({ workspaceId, taskId, onOpenTask }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [commentFor, setCommentFor] = useState(null);
  const [commentText, setCommentText] = useState('');

  const refresh = useCallback(async () => {
    try {
      const r = await taskAPI.legacySubtasks(workspaceId, taskId);
      setSubs(r?.data?.subtasks || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, [workspaceId, taskId]);

  useEffect(() => { if (workspaceId && taskId) refresh(); }, [refresh, workspaceId, taskId]);

  const setStatus = async (sub, status) => {
    setBusyId(sub.id);
    try {
      await taskAPI.update(workspaceId, sub.id, { status });
      toast.success(`"${sub.title}" → ${STATUS_LABEL[status]}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setBusyId(null); }
  };

  const sendComment = async (sub) => {
    if (!commentText.trim()) return;
    setBusyId(sub.id);
    try {
      await api.post(`/tasks/${workspaceId}/${sub.id}/comments`, { body: commentText.trim() });
      toast.success('Comment added');
      setCommentText('');
      setCommentFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Comment failed');
    } finally { setBusyId(null); }
  };

  if (loading || subs.length === 0) return null;
  const done = subs.filter((s) => s.status === 'completed').length;

  return (
    <div className="space-y-2 pt-4 border-t border-border/30" data-testid="legacy-subtasks-section">
      <div className="flex items-center gap-2">
        <Layers className="h-4 w-4 text-gold" />
        <span className="text-sm font-medium">Legacy Subtasks</span>
        <Badge variant="outline" className="text-[10px]" data-testid="legacy-subtasks-count">
          {done}/{subs.length} done
        </Badge>
      </div>
      <p className="text-[11px] text-muted-foreground">
        These items were created as subtasks in the previous system. We no longer create new
        subtasks, but you can still complete and review these remaining items.
      </p>
      <div className="space-y-1.5">
        {subs.map((s) => (
          <div key={s.id} className="rounded-md border border-border/30 px-2.5 py-2" data-testid={`legacy-subtask-${s.id}`}>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onOpenTask && onOpenTask(s.id)}
                title="Open this task (description, links, reference content)"
                className={`text-[12px] flex-1 truncate text-left hover:text-gold hover:underline ${s.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}
                data-testid={`legacy-subtask-open-${s.id}`}
              >
                {s.title}
              </button>
              {s.assignee_names?.length > 0 && (
                <span className="text-[10px] text-muted-foreground truncate max-w-[110px]">{s.assignee_names.join(', ')}</span>
              )}
              {s.due_date && <span className="text-[10px] text-muted-foreground/60">{s.due_date.slice(0, 10)}</span>}
              <Badge variant="outline" className={`text-[9px] uppercase shrink-0 ${STATUS_CLS[s.status] || 'text-foreground/60'}`}>
                {STATUS_LABEL[s.status] || s.status}
              </Badge>
            </div>
            <div className="flex items-center gap-1.5 mt-1.5">
              {s.status !== 'in_progress' && s.status !== 'completed' && (
                <Button size="sm" variant="outline" className="h-6 text-[10px]" disabled={busyId === s.id}
                        onClick={() => setStatus(s, 'in_progress')} data-testid={`legacy-sub-start-${s.id}`}>
                  <Play className="h-3 w-3 mr-0.5" /> Start
                </Button>
              )}
              {s.status !== 'completed' && (
                <Button size="sm" variant="outline" className="h-6 text-[10px] text-emerald-300 border-emerald-500/30" disabled={busyId === s.id}
                        onClick={() => setStatus(s, 'completed')} data-testid={`legacy-sub-complete-${s.id}`}>
                  <CheckCircle2 className="h-3 w-3 mr-0.5" /> Complete
                </Button>
              )}
              <button type="button"
                      onClick={() => { setCommentFor(commentFor === s.id ? null : s.id); setCommentText(''); }}
                      className="text-[10px] text-muted-foreground hover:text-gold inline-flex items-center gap-1"
                      data-testid={`legacy-sub-comment-toggle-${s.id}`}>
                <MessageSquare className="h-3 w-3" /> Comment
              </button>
            </div>
            {commentFor === s.id && (
              <div className="flex items-center gap-1.5 mt-1.5">
                <Input value={commentText} onChange={(e) => setCommentText(e.target.value)}
                       placeholder="Add a note on this item..." className="h-7 text-[12px]"
                       onKeyDown={(e) => { if (e.key === 'Enter') sendComment(s); }}
                       data-testid={`legacy-sub-comment-input-${s.id}`} />
                <Button size="sm" className="h-7 px-2" disabled={busyId === s.id || !commentText.trim()}
                        onClick={() => sendComment(s)} data-testid={`legacy-sub-comment-send-${s.id}`}>
                  {busyId === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
